/**
 * Build ProfileNode tree for flamegraph rendering
 *
 * For HOVER mode (includeAncestors=true):
 *   - Walk up to root to get ancestor path
 *   - From root, build tree following path to hovered node
 *   - Include ALL descendants from hovered node
 *   - Result: Complete tree showing context above and below
 *
 * For CLICK mode (includeAncestors=false):
 *   - Start at clicked node as root
 *   - Traverse all descendants
 *   - Result: Subtree showing only what's below
 */
export function buildSubtreeForFlamegraph(stack, group, timelineData, includeAncestors = true) {
    let nextId = 0;

    // Step 1: If includeAncestors, find root and build ancestor path
    let rootStack = stack;
    let ancestorPath = [];

    if (includeAncestors) {
        // Walk up to root
        let current = stack;
        while (current) {
            ancestorPath.unshift(current); // Add to front
            current = current.parent;
        }
        rootStack = ancestorPath[0]; // Root is first
    } else {
        // For click mode, start at clicked node
        ancestorPath = [stack];
        rootStack = stack;
    }

    // Step 2: Build ProfileNode tree starting from root
    function convertToProfileNode(stackNode, parentId, targetPath = null, targetDepth = 0) {
        const isOnTargetPath = targetPath && targetDepth < targetPath.length &&
                                stackNode === targetPath[targetDepth];
        const isTarget = stackNode === stack;

        const id = nextId++;
        const module = stackNode.frame?.module || 'Root';
        const method = stackNode.frame?.method || '';
        const name = method ? `${module}!${method}` : module;
        const value = getDuration(stackNode, group);

        const node = {
            id,
            parentId,
            name,
            value,
            exclusive: value, // Will be adjusted below
            children: []
        };

        // Step 3: Recurse into children
        if (stackNode.substacks && stackNode.substacks.length > 0) {
            let childValue = 0;

            for (const substack of stackNode.substacks) {
                // For ancestors mode: only follow path to target, then all descendants from target
                // For click mode: include all descendants
                let shouldInclude = false;

                if (!includeAncestors) {
                    // Click mode: all descendants
                    shouldInclude = true;
                } else if (isTarget) {
                    // At target: include all descendants
                    shouldInclude = true;
                } else if (isOnTargetPath && substack === targetPath[targetDepth + 1]) {
                    // On path to target: follow path
                    shouldInclude = true;
                }

                if (shouldInclude) {
                    const child = convertToProfileNode(
                        substack,
                        id,
                        includeAncestors ? targetPath : null,
                        targetDepth + 1
                    );
                    node.children.push(child);
                    childValue += child.value;
                }
            }

            // Exclusive time = self time (total - children)
            node.exclusive = Math.max(0, value - childValue);
        }

        return node;
    }

    const root = convertToProfileNode(
        rootStack,
        -1,
        includeAncestors ? ancestorPath : null,
        0
    );

    return root;
}

/**
 * Reconstruct full stack from root to current frame (for hover)
 * Walks up parent chain to build ancestor path
 * DEPRECATED: Use buildSubtreeForFlamegraph instead
 */
export function reconstructFullStack(stack, group, timelineData) {
    const frames = [];
    let current = stack;

    // Walk up to root
    while (current) {
        frames.unshift({
            module: current.frame?.module || 'Root',
            method: current.frame?.method || '',
            level: current.level,
            duration: getDuration(current, group),
            samples: current.length,
            isRoot: current.level === -1
        });
        current = current.parent;
    }

    return frames;
}

/**
 * Extract descendant subtree from clicked frame (for click)
 * BFS traversal through substacks
 * DEPRECATED: Use buildSubtreeForFlamegraph instead
 */
export function extractSubtree(stack, group, timelineData) {
    const root = {
        module: stack.frame?.module || 'Root',
        method: stack.frame?.method || '',
        level: stack.level,
        duration: getDuration(stack, group),
        samples: stack.length,
        children: []
    };

    // BFS to build tree structure
    const queue = [{ node: root, stackNode: stack }];

    while (queue.length > 0) {
        const { node, stackNode } = queue.shift();

        if (stackNode.substacks && stackNode.substacks.length > 0) {
            for (const substack of stackNode.substacks) {
                const child = {
                    module: substack.frame.module,
                    method: substack.frame.method,
                    level: substack.level,
                    duration: getDuration(substack, group),
                    samples: substack.length,
                    children: []
                };
                node.children.push(child);
                queue.push({ node: child, stackNode: substack });
            }
        }
    }

    return root;
}

/**
 * Calculate duration for a stack frame
 */
function getDuration(stack, group) {
    if (group.type === 0) {
        // OnCPU: duration = number of samples (each 1ms)
        return stack.length;
    } else {
        // OffCPU: duration from group
        return group.duration / 1000; // Convert to ms
    }
}

/**
 * Format module!method name with optional truncation
 */
export function formatFrameName(module, method, maxLength = null) {
    const fullName = `${module}!${method}`;
    if (maxLength && fullName.length > maxLength) {
        return fullName.substring(0, maxLength - 3) + '...';
    }
    return fullName;
}

/**
 * Count total nodes in subtree
 */
export function countSubtreeNodes(subtree) {
    let count = 1; // Count self
    if (subtree.children) {
        for (const child of subtree.children) {
            count += countSubtreeNodes(child);
        }
    }
    return count;
}
